import { Info } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Button,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
  FormControlLabel,
  FormHelperText,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  DialogTitle,
} from '@mui/material';
import {
  FormsErrorDataType,
  SelectedGateways,
} from 'containers/dashboard/Forms/FormListingType';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

type Props = {
  allowCustomProduct: boolean;
  allowDonation: boolean;
  showDonationMemory: boolean;
  donationMemoryLabel: string;
  title: string;
  description: string;
  donateButtonText: string;
  allowRecurringForDonation: boolean;
  allowOnlyRecurringPayments: boolean;
  allowSubscription: boolean;
  methods: SelectedGateways[];
  changeKeyValue: (name: string, value: boolean) => void;
  logo: string;
  onChangeMode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeElement: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | SelectChangeEvent
  ) => void;
  handleDrop: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    name: string
  ) => void;
  stateError: FormsErrorDataType;
};

const DonationSettingsForm: FunctionComponent<Props> = ({
  methods,
  allowDonation,
  donateButtonText,
  showDonationMemory,
  donationMemoryLabel,
  allowCustomProduct,
  allowRecurringForDonation,
  allowOnlyRecurringPayments,
  title,
  description,
  logo,
  changeKeyValue,
  onChangeMode,
  handleDrop,
  stateError,
  onChangeElement,
  allowSubscription,
}) => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { subscription: subscriptionEnabled, formImageTypeUploading } =
    useSelector((state: any) => state.formEditor);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { products } = useSelector((state: any) => state.productsFromEditor);
  const [open, setOpen] = useState(false);

  function checkForKeyValueFalse(methods: SelectedGateways[]) {
    for (let i = 0; i < methods.length; i++) {
      if (methods[i].is_selected && methods[i].name === 'stripe') {
        return true;
      }
    }

    return false;
  }
  const stripeSelected = checkForKeyValueFalse(methods);

  useEffect(() => {
    if (!allowDonation) {
      changeKeyValue('allowRecurringForDonation', false);
    }
  }, [allowDonation, allowSubscription]);

  const handleClose = (booleanFlag: boolean) => {
    if (booleanFlag) {
      changeKeyValue('allowRecurringForDonation', booleanFlag);
    } else {
      changeKeyValue('allowRecurringForDonation', booleanFlag);
    }
    setOpen(false);
  };

  const setAllowRecurringOnce = () => {
    allowOnlyRecurringPayments
      ? changeKeyValue('allowOnlyRecurringPayments', false)
      : changeKeyValue('allowOnlyRecurringPayments', true);
  };

  useEffect(() => {
    if (!allowRecurringForDonation) {
      changeKeyValue('allowOnlyRecurringPayments', false);
    }
  }, [allowRecurringForDonation]);

  return (
    <>
      <Stack direction='column' spacing='30px'>
        <FormControlLabel
          control={
            <Switch
              name='allowDonation'
              checked={allowDonation}
              onChange={onChangeMode}
              disabled={
                subscriptionEnabled || products.length > 0 || allowCustomProduct
              }
            />
          }
          label={t('donation_form')}
        />
        {products.length > 0 && (
          <div className='mt-2 flex items-center text-[14px] text-gray-600'>
            <Info sx={{ width: '15px', height: '15px' }} />{' '}
            <span className='ml-1' style={{ width: '50%' }}>
              {' '}
              {t('delete_all_products')}
            </span>
          </div>
        )}
        {allowDonation ? (
          <>
            <TextField
              id='outlined-basic'
              name='title'
              label={t('enter_title')}
              variant='outlined'
              type='text'
              value={title}
              onChange={onChangeElement}
              error={stateError.title.length ? true : false}
              helperText={stateError.title}
            />
            <TextField
              id='outlined-basic'
              name='description'
              label={t('enter_description')}
              variant='outlined'
              type='text'
              value={description}
              onChange={onChangeElement}
              error={stateError.description.length ? true : false}
              helperText={stateError.description}
            />
            <input
              name='logo'
              accept='image/*'
              style={{ display: 'none' }}
              id='donationImageInput'
              onChange={e => handleDrop(e, 'logo')}
              type='file'
            />
            <label htmlFor='donationImageInput'>
              <LoadingButton
                variant='outlined'
                component='span'
                loading={
                  formImageTypeUploading !== null &&
                  formImageTypeUploading === 'logo'
                }
              >
                {logo ? t('update') : t('upload')} {t('logo')}
              </LoadingButton>
              {stateError.logo.length ? (
                <FormHelperText variant='filled' error={true}>
                  {stateError.logo}
                </FormHelperText>
              ) : (
                ''
              )}
            </label>

            {logo ? (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={logo}
                  alt='title'
                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                />
              </Stack>
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  name='showDonationMemory'
                  checked={showDonationMemory}
                  onChange={onChangeMode}
                />
              }
              label={t('show_hide_in_memory_of')}
            />
            {showDonationMemory ? (
              <TextField
                id='outlined-basic'
                name='donationMemoryLabel'
                label={t('donation_memory_label')}
                variant='outlined'
                type='text'
                value={donationMemoryLabel}
                onChange={onChangeElement}
                error={stateError.donationMemoryLabel.length ? true : false}
                helperText={stateError.donationMemoryLabel}
              />
            ) : null}
            <TextField
              id='outlined-basic'
              name='donateButtonText'
              label={t('donate_button_text')}
              variant='outlined'
              type='text'
              value={donateButtonText}
              onChange={onChangeElement}
              error={stateError.donateButtonText.length ? true : false}
              helperText={stateError.donateButtonText}
            />
          </>
        ) : null}
        <Stack direction='row' spacing='10px'>
          {true && (
            <FormHelperText error={true}>
              {stateError.paymentType}
            </FormHelperText>
          )}
        </Stack>
        {subscriptionEnabled && (
          <div className='mb-2 flex items-center text-[14px] text-gray-600'>
            <Info sx={{ width: '15px', height: '15px' }} />{' '}
            <span className='ml-1'> {t('subscription_enabled_notice')}</span>
          </div>
        )}
        {allowDonation && (
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  name='allowRecurringForDonation'
                  checked={allowRecurringForDonation}
                  onChange={() => {
                    setOpen(true);
                  }}
                  disabled={products.length > 0 || !stripeSelected}
                />
              }
              label={t('allow_recurring')}
            />
            {allowRecurringForDonation && (
              <FormControlLabel
                control={
                  <Checkbox
                    name='allowOnlyRecurringPayments'
                    checked={allowOnlyRecurringPayments}
                    onChange={setAllowRecurringOnce}
                  />
                }
                label={t(`allow_only_recurring`)}
              />
            )}
            {allowRecurringForDonation && (
              <div className='mt-2 flex items-center text-[14px] text-gray-600'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1' style={{ width: '100%' }}>
                  {' '}
                  {t('allow_recurring_is_enabled')}
                </span>
              </div>
            )}
            {!stripeSelected && (
              <div className='flex items-center text-[14px] text-gray-600'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1' style={{ width: '100%' }}>
                  {' '}
                  {t('recurring_can_be_allowed')}
                </span>
              </div>
            )}
          </Stack>
        )}
      </Stack>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle id='alert-dialog-title'>{t('surety_notice')}</DialogTitle>
        <DialogContent className='mt-3'>
          <DialogContentText>
            {allowRecurringForDonation
              ? t('disable_subscription_notice')
              : t('enable_subscription_notice')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              allowRecurringForDonation
                ? handleClose(false)
                : handleClose(true);
            }}
          >
            {t('continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(DonationSettingsForm);
